import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { AdminManagedBadge } from '@calendly/ui/components/admin-managed-badge';
import { Button } from '@calendly/ui/components/button';
import { CheckboxInput } from '@calendly/ui/components/checkbox-input';
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown';
import { IconChevronUp } from '@calendly/ui/components/glyphs/IconChevronUp';
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink';
import { IconWindmill } from '@calendly/ui/components/glyphs/IconWindmill';
import { Popover, PopoverContent, PopoverTrigger } from '@calendly/ui/components/popover';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useProductRefreshAppShell } from 'features/app_shell';
import { useEventType } from 'features/event_type_provider';
import { usePublisherWorkflowsT } from 'services/workflows/publisher_workflows_i18n';
import { getAutomationMenuSubtitle } from './crud_automation_menu_copy';
import { useUpdateWorkflowEventTypesMutation } from '../../../../api';
import { buildWorkflowEventTypeIds, isManagedWorkflow, isWorkflowAttachedToEventType } from '../automation_workflow_utils';

// Popover (not DropdownMenu): multi-select checkboxes + Apply/Cancel footer are not valid
// React Aria Menu collection children and crash when the menu opens.
// Visuals follow Figma Event-type-editor v4 "Automations menu" (node 2780:384174).

const menuCss = "mxmmtd0";

/* PopoverContent svg { fill/stroke } beats equal-specificity resets; pin the
   Manage automations leadingIcon (UiIcon small / 16px, stroke 2) to the outline mock. */
const manageAutomationsButtonCss = "m10rrnnh";
const sectionTitleCss = "sw25qju";
const menuItemCss = "m11vjxa1";
const menuItemLinesCss = "mau5zxb";
const menuItemRowCss = "m12371hp";
const menuItemTitleCss = "m19kvn1";
const checkboxSpacerCss = "c1s4ecwx";
const footerCss = "fvidsgo";
const footerActionsCss = "fmvaael";
const dividerCss = "d13bfgg3";
const spacerCss = "sa0xh7e";
const AutomationMenuItem = ({
  workflow,
  checked,
  onToggle,
  isRebrandEnabled
}) => {
  const checkboxId = `automation-menu-${workflow.uuid}`;
  return <label htmlFor={checkboxId} className={menuItemCss} data-testid={`automation-menu-item-${workflow.uuid}`}>
      <div className={menuItemLinesCss}>
        <div className={menuItemRowCss}>
          <CheckboxInput id={checkboxId} aria-label={workflow.name} checked={checked} onChange={onToggle} />
          <BodyText size="medium" className={menuItemTitleCss}>
            {workflow.name}
          </BodyText>
          {isManagedWorkflow(workflow) ? <AdminManagedBadge iconOnly text="Admin managed" /> : null}
        </div>
        <div className={menuItemRowCss}>
          <div className={checkboxSpacerCss} aria-hidden="true" />
          <BodyText color="muted" size="small" className={menuItemTitleCss}>
            {getAutomationMenuSubtitle(workflow, isRebrandEnabled)}
          </BodyText>
        </div>
      </div>
    </label>;
};
const AutomationMenuSection = ({
  title,
  workflows,
  selectedIds,
  onToggle,
  isRebrandEnabled
}) => {
  if (!workflows.length) return null;
  return <div>
      <div className={sectionTitleCss}>{title}</div>
      {workflows.map(workflow => <AutomationMenuItem key={workflow.uuid} workflow={workflow} checked={selectedIds.has(workflow.uuid)} onToggle={() => onToggle(workflow.uuid)} isRebrandEnabled={isRebrandEnabled} />)}
    </div>;
};

// Anchored multi-select for attaching existing automations to the current event type.
// Shown once the owner has at least one automation in their catalog (v4 ET editor mock).
export const AddAutomationMenu = ({
  automationsUrl,
  availableWorkflows,
  onAutomationsNavigationClick = () => {}
}) => {
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [updateWorkflowEventTypes, {
    isLoading
  }] = useUpdateWorkflowEventTypesMutation();
  const eventType = useEventType();
  const {
    id,
    parent_id: parentId
  } = eventType;
  const eventTypeId = parentId || id;
  const isRebrandEnabled = useProductRefreshAppShell();
  const wfT = usePublisherWorkflowsT();
  const personalWorkflows = useMemo(() => availableWorkflows.filter(workflow => !isManagedWorkflow(workflow)), [availableWorkflows]);
  const managedWorkflows = useMemo(() => availableWorkflows.filter(workflow => isManagedWorkflow(workflow)), [availableWorkflows]);
  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set(availableWorkflows.filter(workflow => isWorkflowAttachedToEventType(workflow, eventTypeId, parentId)).map(workflow => workflow.uuid)));
  }, [isOpen, availableWorkflows, eventTypeId, parentId]);
  const handleToggle = workflowUuid => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(workflowUuid)) {
        next.delete(workflowUuid);
      } else {
        next.add(workflowUuid);
      }
      return next;
    });
  };
  const handleApply = async () => {
    const changedWorkflows = availableWorkflows.filter(workflow => {
      const shouldAttach = selectedIds.has(workflow.uuid);
      const isAttached = isWorkflowAttachedToEventType(workflow, eventTypeId, parentId);
      return shouldAttach !== isAttached;
    });
    try {
      await Promise.all(changedWorkflows.map(workflow => updateWorkflowEventTypes({
        workflow,
        eventTypeIds: buildWorkflowEventTypeIds(workflow, eventTypeId, selectedIds.has(workflow.uuid)),
        workflowsExtractionEnabled
      }).unwrap()));
    } catch {
      // updateWorkflowEventTypes shows the error toast in onQueryStarted
      return;
    }
    setIsOpen(false);
  };
  const myAutomationsTitle = wfT(isRebrandEnabled ? 'publisher.workflows_app_shell_rebrand.tabs.my_workflows' : 'publisher.workflows.my_workflows');
  const adminManagedTitle = wfT(isRebrandEnabled ? 'publisher.workflows_app_shell_rebrand.admin_managed_workflows' : 'publisher.workflows.admin_managed_workflows');
  const showSectionDivider = personalWorkflows.length > 0 && managedWorkflows.length > 0;
  return <Popover isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <PopoverTrigger>
        <Button decoration="secondary-outline" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} aria-haspopup="dialog" data-testid="add-automation-menu-trigger">
          <UiIcon icon={IconWindmill} size="small" title="" />
          {I18n.t('publisher.event_type_editor.sections.more_options.notifications.add_an_automation')}
          <UiIcon icon={isOpen ? IconChevronUp : IconChevronDown} size="small" title="" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={menuCss} placement="bottom start" data-testid="add-automation-menu">
        <AutomationMenuSection title={myAutomationsTitle} workflows={personalWorkflows} selectedIds={selectedIds} onToggle={handleToggle} isRebrandEnabled={isRebrandEnabled} />
        {showSectionDivider ? <>
            <div className={spacerCss} aria-hidden="true" />
            <div className={dividerCss} role="separator" />
          </> : null}
        <AutomationMenuSection title={adminManagedTitle} workflows={managedWorkflows} selectedIds={selectedIds} onToggle={handleToggle} isRebrandEnabled={isRebrandEnabled} />
        <div className={spacerCss} aria-hidden="true" />
        <div className={dividerCss} role="separator" />
        <div className={spacerCss} aria-hidden="true" />
        <div className={footerCss}>
          <span className={manageAutomationsButtonCss}>
            <Button as={RouterLink} to={automationsUrl} target="_blank" rel="noopener noreferrer" size="small" decoration="ghost" leadingIcon={IconExternalLink} onClick={event => onAutomationsNavigationClick(automationsUrl, event)}>
              {I18n.t('publisher.event_type_editor.sections.more_options.notifications.manage_automations')}
            </Button>
          </span>
          <div className={footerActionsCss}>
            <Button size="small" decoration="secondary-outline" onClick={() => setIsOpen(false)}>
              {I18n.t('publisher.action.cancel')}
            </Button>
            <Button size="small" onClick={handleApply} loading={isLoading}>
              {I18n.t('publisher.action.apply')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>;
};

require("./add_automation_menu.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./add_automation_menu.js");